import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { ApiResponse } from "@/services/api/types";
import { CoreService } from "@/services/core/CoreService";
import { StateItem, StateRequest, StateResponse, StateShopper } from "@/types/state";

interface ChangingRoom {
  id: number;
  name: string;
  storeId: number;
  groupId: number;
  assignedAt: number;
}

interface ChangingRoomGroup {
  id: number;
  name: string;
  storeId: number;
}

export interface ChangingRoomArea {
  changingRoomGroup: ChangingRoomGroup;
  changingRooms: ChangingRoom[];
}

interface StateParams {
  roomIds?: number[];
  groupIds?: number[];
  includeStandaloneShoppers?: boolean;
  includeOnHoldShoppers?: boolean;
}

export class LiveViewService extends BaseApiService {
  private static instance: LiveViewService;
  protected readonly coreService: CoreService;
  private readonly authorization: string;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
    const user = this.coreService.getStoredUser();
    this.authorization = user?.token || "";
  }

  public static getInstance(): LiveViewService {
    if (!LiveViewService.instance) {
      LiveViewService.instance = new LiveViewService();
    }
    return LiveViewService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getChangingRoomData(storeId: string): Promise<ApiResponse<ChangingRoomArea[]>> {
    return this.get<ApiResponse<ChangingRoomArea[]>>(
      `/changingRoomGroup/store/${storeId}`,
      this.getCoreBaseUrl(),
    );
  }

  async getState(params: StateParams): Promise<StateResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params) {
        // Add roomIds
        if (params.roomIds?.length) {
          params.roomIds.forEach((id) => {
            queryParams.append("roomIds", id.toString());
          });
        }

        // Add groupIds
        if (params.groupIds?.length) {
          params.groupIds.forEach((id) => {
            queryParams.append("groupIds", id.toString());
          });
        }

        // Add boolean parameters
        if (params.includeStandaloneShoppers !== undefined) {
          queryParams.append(
            "includeStandaloneShoppers",
            params.includeStandaloneShoppers.toString(),
          );
        }
        if (params.includeOnHoldShoppers !== undefined) {
          queryParams.append("includeOnHoldShoppers", params.includeOnHoldShoppers.toString());
        }
      }

      const endpoint = `/state${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

      return await this.get<StateResponse>(endpoint, this.getCoreBaseUrl());
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get shopper by changing room ID
   * @param state - The state response containing rooms and shoppers data
   * @param changingRoomId - The ID of the changing room to find the shopper for
   * @returns The shopper associated with the room, or undefined if not found
   */
  public getShopperByRoom(state: StateResponse, changingRoomId: number): StateShopper | undefined {
    // Find the room with the given ID
    const room = state.data.rooms.find((room) => room.id === changingRoomId);

    // If room not found or has no shopperId, return undefined
    if (!room || !room.shopperId) {
      return undefined;
    }

    // Find the shopper in the shoppers array by ID
    return state.data.shoppers.find((shopper) => shopper.id === room.shopperId);
  }

  /**
   * Get items by changing room ID
   * @param state - The state response containing rooms and shoppers data
   * @param changingRoomId - The ID of the changing room to find items for
   * @returns The items associated with the shopper in the room (filtered to StateItem[] only), or an empty array if not found
   */
  public getItemsByRoom(state: StateResponse, changingRoomId: number): StateItem[] {
    const shopper = this.getShopperByRoom(state, changingRoomId);

    if (!shopper || !shopper.items) {
      return [];
    }

    // Filter to return only StateItem objects, not strings
    return shopper.items.filter(
      (item): item is StateItem => typeof item === "object" && item !== null && "sku" in item,
    );
  }

  /**
   * Get requests by changing room ID
   * @param state - The state response containing rooms and shoppers data
   * @param changingRoomId - The ID of the changing room to find requests for
   * @returns The shopper's requests when the room is occupied, or requests attached to the vacant room
   */
  public getRequestsByRoom(state: StateResponse, changingRoomId: number): StateRequest[] {
    const shopper = this.getShopperByRoom(state, changingRoomId);

    if (!shopper) {
      return state.data.requests.filter((request) => request.changingRoomId === changingRoomId);
    }

    return state.data.requests.filter((request) => request.createdBy === shopper.id);
  }

  // Add more live view-related methods here
}
